import React, { useEffect, useState } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import CustomSelectOption from '../../base_component/CustomSelectOption/CustomSelectOption'
import EllipsisLoader from '../../base_component/EllipsisLoader/EllipsisLoader'
import { getProfileAPI, updateProfileAPI } from '../../service/ApiServices'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'
import { useProfile } from '../../context/ProfileContext'
import { validateUrl } from '../../base_component/Common/data';
import { X } from 'lucide-react'
import ImageCropModal from '../../components/ProfileImageCrop';

export default function MyAccount() {
    const [profile, setProfile] = useState({})
    const [studioId, setStudioId] = useState(null)
    const [profilePicture, setProfilePicture] = useState(null)
    const [profilePicturePreview, setProfilePicturePreview] = useState(null)
    const [showCropModal, setShowCropModal] = useState(false)
    const [tempImageFile, setTempImageFile] = useState(null)
    const { setProfilePic } = useProfile()
    const navigate = useNavigate()

    const initialValues = {
        firstName: profile?.first_name || '',
        lastName: profile?.last_name || '',
        email: profile?.email || '',
        medium: profile?.mediums?.filter(medium => medium.is_enabled).map(medium => medium.medium_name) || [],
        bio: profile?.bio || ''
    }

    const getProfile = () => {
        getProfileAPI()
            .then((res) => {
                if (res?.data?.status === 1) {
                    const profileData = res.data.data.user || {};
                    setStudioId(res?.data?.data?.studio?.id || null)
                    setProfile(profileData);
                    setProfilePic(profileData?.profile_image);
                    localStorage.setItem("plan_start_date", res?.data?.data?.user?.plan_start_date);
                    localStorage.setItem("plan_end_date", res?.data?.data?.user?.plan_end_date);
                } else {
                    toast.error(res?.data?.message || 'Failed to fetch profile');
                }
            })
            .catch((err) => {
                let message = err?.response?.data?.message || 'Failed to fetch profile';
                toast.error(message);
                if (err?.response?.status === 401) {
                    localStorage.removeItem('hoast_token')
                    localStorage.removeItem('hoast_refresh_token')
                    navigate('/login');
                }
            })
    }

    useEffect(() => {
        getProfile();
    }, []);

    const validationSchema = Yup.object({
        firstName: Yup.string()
            .required('First name is required')
            .min(2, 'First name must be at least 2 characters')
            .max(50, 'First name must be less than 50 characters'),
        lastName: Yup.string()
            .required('Last name is required')
            .min(2, 'Last name must be at least 2 characters')
            .max(50, 'Last name must be less than 50 characters'),
        email: Yup.string()
            .email('Please enter a valid email address')
            .required('Email is required'),
        medium: Yup.array()
            .required('Medium is required'),
        bio: Yup.string()
            .required('Bio is required')
            .min(10, 'Bio must be at least 10 characters')
            .max(500, 'Bio must be less than 500 characters')
    })

    const areAllValuesFilled = (values) => {
        return values.firstName &&
            values.lastName &&
            values.email &&
            values.medium &&
            values.medium.length > 0 &&
            values.bio &&
            values.bio.trim().length >= 10
    }

    useEffect(() => {
        if (profile?.profile_image) {
            setProfilePicturePreview(validateUrl(profile?.profile_image) || null)
        }
    }, [profile])

    const handleFileChange = (e) => {
        const file = e.target.files[0]
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                alert('File size must be less than 5MB')
                return
            }
            setTempImageFile(file);
            setShowCropModal(true);
        }
    }

    const handleSubmit = (values, { setSubmitting }) => {

        const selectedMediumIds = profile?.mediums
            ?.filter(medium => values.medium.includes(medium.medium_name))
            ?.map(medium => medium.id)
            ?.join(',') || '';

        const formData = new FormData();
        formData.append('first_name', values.firstName);
        formData.append('last_name', values.lastName);
        formData.append('bio', values.bio);
        formData.append('medium_ids', selectedMediumIds);
        formData.append('studio_id', studioId || '');

        if (profilePicture && typeof profilePicture === 'object') {
            formData.append('profile_image', profilePicture);
        } else if (profilePicture === null) {
            const emptyFile = new File([], '');
            formData.append('profile_image', emptyFile);
        }

        updateProfileAPI(formData)
            .then((response) => {
                if (response?.data?.status === 1) {
                    toast.success(response?.data?.message);
                    getProfile();
                } else {
                    toast.error(response?.data?.message || 'Failed to update profile');
                }
            })
            .catch((error) => {
                const message = error?.response?.data?.message || 'Failed to update profile';
                toast.error(message);

                if (error?.response?.status === 401) {
                    localStorage.removeItem('hoast_token');
                    localStorage.removeItem('hoast_refresh_token');
                    navigate('/login');
                }
            })
            .finally(() => {
                setSubmitting(false);
            });
    }

    const removeProfilePicture = () => {
        setProfilePicturePreview(null);
        setProfilePicture(null);
    };

    const handleCropComplete = (croppedImageUrl, croppedImageBlob) => {
        setProfilePicturePreview(croppedImageUrl);
        const croppedFile = new File([croppedImageBlob], 'profile-image.jpg', { type: 'image/jpeg' });
        setProfilePicture(croppedFile);
        setShowCropModal(false);
    };

    const handleCropCancel = () => {
        setShowCropModal(false);
        setTempImageFile(null);
    };

    return (
        <div className="bg-white">
            <div className="max-w-4xl">
                <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting, setFieldValue, values, errors, touched, handleBlur }) => {
                        const allFilled = areAllValuesFilled(values)

                        return (
                            <Form className="space-y-6">
                                <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`px-8 sm:w-[200px] w-full h-[52px] rounded-xl font-medium transition-colors ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : allFilled
                                                ? 'bg-[#212121] text-white hover:bg-[#1a1a1a]'
                                                : 'bg-[#F0F1F3] text-black cursor-not-allowed'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <EllipsisLoader color="white" />
                                        ) : (
                                            'Save'
                                        )}
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">

                                    <label className="text-sm font-medium text-[#667085] md:col-span-1">
                                        Profile Picture
                                    </label>
                                    <div className="md:col-span-2">
                                        <div className="flex items-center space-x-4">
                                            <div className="relative">
                                                <div className="w-28 h-28 rounded-full overflow-hidden bg-gray-200">
                                                    {profilePicturePreview ? (
                                                        <img
                                                            src={profilePicturePreview}
                                                            alt="Profile"
                                                            className="w-full shadow-xl h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center">
                                                            <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    )}
                                                </div>
                                                <input
                                                    type="file"
                                                    accept="image/png, image/jpg, image/jpeg"
                                                    onChange={handleFileChange}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                                />
                                                {profilePicturePreview && (
                                                    <button
                                                        type="button"
                                                        onClick={removeProfilePicture}
                                                        className="absolute bg-gray-100 shadow-xl rounded-full top-0 right-1  p-1 text-red-500 "
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                )}
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    onClick={() => document.querySelector('input[type="file"]').click()}
                                                    className="text-sm whitespace-nowrap bg-gray-100 rounded-xl p-3 font-medium"
                                                >
                                                    Change photo
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <label className="text-sm font-medium text-[#667085] md:col-span-1">
                                        First Name & Last Name
                                    </label>
                                    <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <Field
                                                type="text"
                                                name="firstName"
                                                placeholder="First Name"
                                                className="mt-1 block text-sm w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:border focus:outline-none focus:border-[#24B565]"
                                            />
                                            <ErrorMessage name="firstName" component="div" className="text-red-500 text-xs mt-1" />
                                        </div>
                                        <div>
                                            <Field
                                                type="text"
                                                name="lastName"
                                                placeholder="Last Name"
                                                className="mt-1 block text-sm w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:border focus:outline-none focus:border-[#24B565]"
                                            />
                                            <ErrorMessage name="lastName" component="div" className="text-red-500 text-xs mt-1" />
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <label className="text-sm font-medium text-[#667085] md:col-span-1">
                                        Email
                                    </label>
                                    <div className="md:col-span-2">
                                        <Field
                                            type="email"
                                            name="email"
                                            disabled
                                            className="mt-1 block text-sm w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                                            placeholder="Email"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                                    </div>
                                </div>

                                <CustomSelectOption
                                    label="Medium"
                                    name="medium"
                                    value={values.medium}
                                    onChange={(e) => setFieldValue("medium", e.target.value)}
                                    onBlur={handleBlur}
                                    error={errors.medium}
                                    touched={touched.medium}
                                    options={profile?.mediums?.map(medium => ({
                                        label: medium.medium_name,
                                        value: medium.medium_name
                                    })) || []}
                                />

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                                    <label className="text-sm font-medium text-[#667085] md:col-span-1">
                                        Short Bio
                                    </label>
                                    <div className="md:col-span-2">
                                        <div className="">
                                            <Field
                                                as="textarea"
                                                name="bio"
                                                rows={4}
                                                placeholder="Tell us about yourself..."
                                                className="mt-1 block text-sm w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm focus:border focus:outline-none focus:border-[#24B565] resize-none"
                                            />
                                        </div>
                                        <div className="flex justify-between items-center mt-1">
                                            <div>
                                                <ErrorMessage name="bio" component="div" className="text-red-500 text-xs" />
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {values.bio?.length || 0}/500
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* <div className="flex justify-end">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`px-8 sm:w-[200px] w-full h-[52px] rounded-xl font-medium transition-colors ${isSubmitting
                                            ? 'bg-gray-400 cursor-not-allowed text-white'
                                            : allFilled
                                                ? 'bg-[#212121] text-white hover:bg-[#1a1a1a]'
                                                : 'bg-[#F0F1F3] text-black cursor-not-allowed'
                                            }`}
                                    >
                                        {isSubmitting ? (
                                            <EllipsisLoader color="white" />
                                        ) : (
                                            'Save'
                                        )}
                                    </button>
                                </div> */}
                            </Form>
                        )
                    }}
                </Formik>
            </div>

            <ImageCropModal
                isOpen={showCropModal}
                onClose={handleCropCancel}
                imageSrc={tempImageFile ? URL.createObjectURL(tempImageFile) : ''}
                onCropComplete={handleCropComplete}
                className="profile-image-crop-modal"
            />
        </div>
    )
}
